/**
 * PaperTradingTab — Stage 1
 *
 * Renders one StrategyBlock per variant. Right now only V1 (NG KAMA) is wired.
 * Later variants get their own block stacked below (same component, different
 * endpoint props). Polling: signal + paper-book every 15 s, desk-status every 30 s.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getDeskStatus, getNgSignal, getNgPaperBook, getSocketEvents } from '../../api/desk';

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmtIst(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function timeAgo(iso) {
  if (!iso) return null;
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)  return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function fmt2(v) {
  if (v == null) return '—';
  return Number(v).toFixed(2);
}

function fmtPnl(v) {
  if (v == null) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

// ── Shared atom components ────────────────────────────────────────────────────

function Row({ label, children, style }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 6,
      padding: '5px 0', ...style,
    }}>
      <span style={{ fontSize: 10, color: 'var(--ink-4)', flexShrink: 0, minWidth: 88 }}>
        {label}
      </span>
      <span style={{ fontSize: 11, color: 'var(--ink-2)', fontFamily: 'var(--font-data, monospace)' }}>
        {children}
      </span>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '.07em',
      textTransform: 'uppercase', color: 'var(--ink-4)',
      marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

function Pill({ label, color, bg }) {
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 9.5, fontWeight: 700, letterSpacing: '.05em',
      textTransform: 'uppercase',
      padding: '2px 7px', borderRadius: 'var(--r-2)',
      color: color || 'var(--ink-3)',
      background: bg || 'var(--surface-2)',
    }}>
      {label}
    </span>
  );
}

// ── Status pills ──────────────────────────────────────────────────────────────

const ENGINE_STATUS_META = {
  ACTIVE:   { color: '#1d7a3c', bg: 'rgba(45,138,80,.12)'   },
  WAITING:  { color: 'var(--ink-3)', bg: 'var(--surface-2)' },  // healthy, not an error
  NO_DATA:  { color: '#8f3e3e', bg: 'rgba(168,93,87,.10)'   },
};

function EngineStatusPill({ status }) {
  const m = ENGINE_STATUS_META[status] || ENGINE_STATUS_META.NO_DATA;
  return <Pill label={status || '—'} color={m.color} bg={m.bg} />;
}

function DirPill({ dir }) {
  if (!dir) return <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>—</span>;
  const isLong = dir === 'LONG';
  return (
    <Pill
      label={dir}
      color={isLong ? '#1d7a3c' : '#8f3e3e'}
      bg={isLong ? 'rgba(45,138,80,.12)' : 'rgba(168,93,87,.10)'}
    />
  );
}

// ── Staleness colouring for last_bar_ts ──────────────────────────────────────
// Market hours 09:00–23:30 IST. During session: stale if > 90 min.
// Outside session: show neutral dim.

function barTsColor(iso, inSession) {
  if (!iso) return 'var(--ink-4)';
  if (!inSession) return 'var(--ink-3)';
  const ageMin = (Date.now() - new Date(iso).getTime()) / 60000;
  if (ageMin > 90) return '#b85555';   // red — stale mid-session
  if (ageMin > 60) return '#c8922a';   // amber — getting old
  return 'var(--ink-2)';
}

// ── Token expiry helpers ──────────────────────────────────────────────────────

function tokenLabel(deskStatus) {
  if (!deskStatus) return { text: '—', warn: false };
  const exp = deskStatus.token_expires_at;
  if (!exp) return { text: 'no expiry data', warn: true };
  const msLeft = new Date(exp).getTime() - Date.now();
  const expStr = fmtIst(exp);
  if (msLeft < 0) return { text: `expired ${expStr}`, warn: true };
  const hLeft = Math.floor(msLeft / 3600000);
  const mLeft = Math.floor((msLeft % 3600000) / 60000);
  const countdown = hLeft > 0 ? `${hLeft}h ${mLeft}m` : `${mLeft}m`;
  return { text: `valid · expires ${expStr} (${countdown})`, warn: hLeft < 1 };
}

// ── Socket health helpers ─────────────────────────────────────────────────────

// Parse socket_events rows into a structured summary.
function parseSocketHealth(events, deskStatus) {
  const connected = deskStatus?.socket_connected ?? null;

  // Walk newest-first to find:
  // - when we entered current state (last on_connect or on_close/on_noreconnect)
  // - last close reason
  // - current reconnect attempt count if thrashing
  let stateTs = null;       // ISO ts of the event that set current state
  let lastCloseDetail = null;
  let currentAttempt = null;
  let phase = null;

  // Latest watchdog_rearm tells us the attempt count if disconnected
  const latestRearm = events.find(e => e.event === 'watchdog_rearm');
  if (latestRearm && !connected) {
    // Extract attempt from detail: "phase=backstop attempt=14 reason=..."
    const m = latestRearm.detail?.match(/attempt=(\d+)/);
    if (m) currentAttempt = parseInt(m[1], 10);
    const pm = latestRearm.detail?.match(/phase=(\w+)/);
    if (pm) phase = pm[1];
  }

  // Find last on_close for close reason
  const lastClose = events.find(e => e.event === 'on_close');
  if (lastClose) lastCloseDetail = lastClose.detail;

  // Find the event that set the current state
  if (connected) {
    const ev = events.find(e => e.event === 'on_connect');
    if (ev) stateTs = ev.ts_ist;
  } else {
    const ev = events.find(e =>
      e.event === 'on_close' || e.event === 'on_noreconnect' || e.event === 'on_error'
    );
    if (ev) stateTs = ev.ts_ist;
  }

  return { connected, stateTs, lastCloseDetail, currentAttempt, phase };
}

// How long ago was an ISO ts (returns e.g. "4m 12s", "2h 3m", "just now")
function sinceIso(isoStr) {
  if (!isoStr) return null;
  const diffMs = Date.now() - new Date(isoStr).getTime();
  if (diffMs < 0) return null;
  const s = Math.floor(diffMs / 1000);
  if (s < 5)  return 'just now';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), rs = s % 60;
  if (m < 60) return rs > 0 ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60), rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

// ── HEALTH section ────────────────────────────────────────────────────────────

function SocketRow({ deskStatus, socketEvents }) {
  const { connected, stateTs, lastCloseDetail, currentAttempt, phase } =
    parseSocketHealth(socketEvents || [], deskStatus);
  const duration = sinceIso(stateTs);

  // Alarm: disconnected and we have events (i.e. we're logged in and it's dead)
  const alarm = connected === false && socketEvents && socketEvents.length > 0;

  if (connected === null && (!socketEvents || socketEvents.length === 0)) {
    return (
      <Row label="Socket">
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      </Row>
    );
  }

  if (connected) {
    return (
      <Row label="Socket">
        <span style={{ color: '#1d7a3c', fontWeight: 500 }}>connected</span>
        {duration && (
          <span style={{ fontSize: 10, color: 'var(--ink-4)', marginLeft: 6 }}>
            · {duration}
          </span>
        )}
      </Row>
    );
  }

  // Disconnected — alarm state
  // Build status line: "disconnected · re-arming, attempt 14 (backstop)"
  let statusLine = 'disconnected';
  if (currentAttempt != null) {
    statusLine += ` · re-arming, attempt ${currentAttempt}`;
    if (phase) statusLine += ` (${phase})`;
  }
  if (duration) statusLine += ` · ${duration}`;

  // Parse close detail: "code=1006 reason=None" or similar
  let closeDisplay = lastCloseDetail;
  if (closeDisplay && closeDisplay.length > 80) {
    closeDisplay = closeDisplay.slice(0, 80) + '…';
  }

  return (
    <>
      {/* Amber alarm banner */}
      <div style={{
        margin: '6px 0 4px',
        padding: '7px 10px',
        background: 'rgba(180, 120, 0, 0.12)',
        border: '1px solid rgba(180, 120, 0, 0.35)',
        borderRadius: 5,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Pulsing amber dot */}
          <span style={{
            display: 'inline-block',
            width: 8, height: 8,
            borderRadius: '50%',
            background: '#c98000',
            flexShrink: 0,
            animation: 'socketAlarmPulse 1.4s ease-in-out infinite',
          }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#c98000' }}>
            SOCKET {statusLine.toUpperCase()}
          </span>
        </div>
        {closeDisplay && (
          <div style={{
            marginTop: 4,
            fontSize: 10,
            color: 'rgba(180, 120, 0, 0.85)',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}>
            Last close: {closeDisplay}
          </div>
        )}
      </div>
      <style>{`
        @keyframes socketAlarmPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.35; }
        }
      `}</style>
    </>
  );
}

function HealthSection({ signal, deskStatus, socketEvents }) {
  const s = signal || {};
  const inSession = deskStatus?.in_session ?? false;

  const barColor  = barTsColor(s.last_bar_ts, inSession);
  const ago       = timeAgo(s.last_bar_ts);
  const barLabel  = s.last_bar_ts
    ? `${fmtIst(s.last_bar_ts)}${ago ? ` · ${ago}` : ''}`
    : '—';

  const tok = tokenLabel(deskStatus);

  return (
    <div style={{ padding: '10px 14px', borderBottom: '.5px solid var(--hair)' }}>
      <SectionLabel>Health</SectionLabel>

      <Row label="Engine">
        <EngineStatusPill status={s.status} />
        {s.status === 'NO_DATA' && s.skip_reason && (
          <span style={{ fontSize: 10, color: '#8f3e3e', marginLeft: 6 }}>
            {s.skip_reason}
          </span>
        )}
        {s.status === 'WAITING' && (
          <span style={{ fontSize: 10, color: 'var(--ink-4)', marginLeft: 6 }}>
            running · awaiting first cross
          </span>
        )}
      </Row>

      <Row label="Last bar">
        <span style={{ color: barColor }}>{barLabel}</span>
        {inSession && barColor === '#b85555' && (
          <span style={{ fontSize: 10, color: '#b85555', marginLeft: 6 }}>
            STALE mid-session
          </span>
        )}
      </Row>

      <Row label="KAMA date">
        {s.kama_date || '—'}
        <span style={{ fontSize: 10, color: 'var(--ink-4)', marginLeft: 6 }}>
          (completed daily bar)
        </span>
      </Row>

      <Row label="Kite token">
        <span style={{ color: tok.warn ? '#b85555' : 'var(--ink-2)' }}>
          {tok.text}
        </span>
      </Row>

      <SocketRow deskStatus={deskStatus} socketEvents={socketEvents} />
    </div>
  );
}

// ── SIGNAL section ────────────────────────────────────────────────────────────

function SignalSection({ signal }) {
  const s = signal || {};
  const ema   = s.last_ema3;
  const kama  = s.last_kama;
  const gap   = (ema != null && kama != null) ? (ema - kama) : null;
  const gapStr = gap != null
    ? `${gap >= 0 ? '+' : ''}${gap.toFixed(2)}`
    : null;
  const gapColor = gap == null ? 'var(--ink-4)'
    : gap > 0 ? '#1d7a3c'
    : '#8f3e3e';

  const lc = s.last_cross;

  return (
    <div style={{ padding: '10px 14px', borderBottom: '.5px solid var(--hair)' }}>
      <SectionLabel>Signal</SectionLabel>

      <Row label="Direction">
        <DirPill dir={s.current_dir} />
      </Row>

      <Row label="EMA3 · KAMA">
        {ema != null ? (
          <>
            <span>EMA {fmt2(ema)}</span>
            <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>·</span>
            <span>KAMA {fmt2(kama)}</span>
            <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>·</span>
            <span style={{ color: gapColor, fontWeight: 600 }}>
              gap {gapStr}
            </span>
          </>
        ) : (
          <span style={{ color: 'var(--ink-4)' }}>—</span>
        )}
      </Row>

      <Row label="Last cross">
        {!lc ? (
          <span style={{ color: 'var(--ink-4)' }}>none yet</span>
        ) : (
          <>
            <DirPill dir={lc.direction} />
            <span style={{ marginLeft: 6 }}>{fmtIst(lc.bar_ts)}</span>
            <span style={{ color: 'var(--ink-4)', margin: '0 6px' }}>·</span>
            <span>EMA {fmt2(lc.ema3)}</span>
            <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>·</span>
            <span>KAMA {fmt2(lc.kama)}</span>
            <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>·</span>
            <span>expiry {lc.expiry || '—'}</span>
          </>
        )}
      </Row>
    </div>
  );
}

// ── BOOK section (Stage 1 — thin summary) ────────────────────────────────────

function BookSection({ book }) {
  const b   = book || {};
  const agg = b.aggregate || {};
  const op  = b.open_position;

  const cumPnl = agg.cumulative_pnl;
  const pnlColor = cumPnl == null ? 'var(--ink-4)'
    : cumPnl >= 0 ? '#1d7a3c'
    : '#8f3e3e';

  return (
    <div style={{ padding: '10px 14px' }}>
      <SectionLabel>Book (paper)</SectionLabel>

      <Row label="Open position">
        {!op ? (
          <span style={{ color: 'var(--ink-4)' }}>none</span>
        ) : (
          <>
            <DirPill dir={op.direction} />
            <span style={{ marginLeft: 6 }}>
              {op.buy_strike}↑ / {op.sell_strike}↑
            </span>
            <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>·</span>
            <span>net debit {fmt2(op.entry_net_debit)}</span>
            {op.unrealised_pnl != null && (
              <>
                <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>·</span>
                <span style={{ color: op.unrealised_pnl >= 0 ? '#1d7a3c' : '#8f3e3e' }}>
                  {fmtPnl(op.unrealised_pnl)} unrealised
                </span>
              </>
            )}
          </>
        )}
      </Row>

      <Row label="Closed trades">
        <span>{agg.n_trades ?? 0}</span>
        {agg.n_skipped > 0 && (
          <span style={{ color: 'var(--ink-4)', marginLeft: 6 }}>
            · {agg.n_skipped} skipped
          </span>
        )}
        {agg.n_null_pnl > 0 && (
          <span style={{ color: 'var(--ink-4)', marginLeft: 6 }}>
            · {agg.n_null_pnl} no-price
          </span>
        )}
      </Row>

      <Row label="Cumulative P&L">
        <span style={{ color: pnlColor, fontWeight: 600 }}>
          {cumPnl != null ? fmtPnl(cumPnl) : '—'}
        </span>
        {agg.win_rate != null && (
          <span style={{ color: 'var(--ink-4)', marginLeft: 6, fontSize: 10 }}>
            · win rate {(agg.win_rate * 100).toFixed(0)}%
          </span>
        )}
      </Row>
    </div>
  );
}

// ── StrategyBlock ─────────────────────────────────────────────────────────────
// One block per variant. Polls its own signal + paper-book endpoints.
// deskStatus is shared across all blocks (passed from parent).

function StrategyBlock({ label, fetchSignal, fetchBook, deskStatus, socketEvents }) {
  const [signal,   setSignal]   = useState(null);
  const [book,     setBook]     = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [error,    setError]    = useState(null);

  const load = useCallback(async () => {
    try {
      const [sig, bk] = await Promise.all([fetchSignal(), fetchBook()]);
      setSignal(sig);
      setBook(bk);
      setLastFetch(new Date().toISOString());
      setError(null);
    } catch (e) {
      setError(e.message || 'fetch error');
    }
  }, [fetchSignal, fetchBook]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  const fetchedAgo = lastFetch ? timeAgo(lastFetch) : null;

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '9px 14px',
        borderBottom: '.5px solid var(--hair)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-1)' }}>
            {label}
          </span>
          {signal && <EngineStatusPill status={signal.status} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {error && (
            <span style={{ fontSize: 10, color: '#b85555' }}>
              {error}
            </span>
          )}
          {fetchedAgo && !error && (
            <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>
              updated {fetchedAgo}
            </span>
          )}
        </div>
      </div>

      {signal ? (
        <>
          <HealthSection signal={signal} deskStatus={deskStatus} socketEvents={socketEvents} />
          <SignalSection signal={signal} />
          <BookSection   book={book} />
        </>
      ) : error ? (
        <div style={{ padding: '20px 14px', color: '#b85555', fontSize: 11 }}>
          {error}
        </div>
      ) : (
        <div style={{ padding: '20px 14px', color: 'var(--ink-4)', fontSize: 11 }}>
          Loading…
        </div>
      )}
    </div>
  );
}

// ── PaperTradingTab ───────────────────────────────────────────────────────────

export default function PaperTradingTab() {
  const [deskStatus,   setDeskStatus]   = useState(null);
  const [socketEvents, setSocketEvents] = useState(null);

  const loadDesk = useCallback(async () => {
    try {
      const d = await getDeskStatus();
      setDeskStatus(d);
    } catch (_) {}
  }, []);

  const loadSocketEvents = useCallback(async () => {
    try {
      const r = await getSocketEvents(60);
      setSocketEvents(r.rows || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadDesk(); loadSocketEvents(); }, [loadDesk, loadSocketEvents]);

  // desk status every 30s; socket events every 15s (need to catch reconnect storms promptly)
  useEffect(() => {
    const t = setInterval(loadDesk, 30_000);
    return () => clearInterval(t);
  }, [loadDesk]);

  useEffect(() => {
    const t = setInterval(loadSocketEvents, 15_000);
    return () => clearInterval(t);
  }, [loadSocketEvents]);

  return (
    <div style={{ padding: '12px 0' }}>
      <StrategyBlock
        label="NG V1 · KAMA trend-filter"
        fetchSignal={getNgSignal}
        fetchBook={getNgPaperBook}
        deskStatus={deskStatus}
        socketEvents={socketEvents}
      />
      {/* V2, V3 blocks will be stacked here as additional <StrategyBlock /> entries */}
    </div>
  );
}
